import React, { useState, useEffect } from 'react';
import { Link, useAuthState, useAuthenticated, useTranslate, useGetList, RichTextField, useLocaleState } from 'react-admin';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import AddIcon from '@mui/icons-material/Add';
import { lighten } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import IconCard from './common/IconCard';
import IconButton from './common/buttons/IconButton';
import DonateButton from './common/buttons/DonateButton';
import EventCard from './events/EventCard';

import events from './events';
import { fetchAdminDashboardStats, fetchMessages, getAccount } from '../services/api';

const sxVideoContainer = {
    position: 'relative',
    width: '100%',
    paddingBottom: '56.25%',
    '& iframe': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        border: 0
    }
};

const sxDonateBox = {
    width: '100%',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'success.main',
    '& .MuiAlert-action button': {
        backgroundColor: '#000',
        color: '#FFF',
        padding: '1px',
        '& svg': {
            width: '13px',
            height: '13px'
        }
    }
};

const NOW = new Date();

const Dashboard = () => {
    const [stats, setStats] = useState();
    const [account, setAccount] = useState();
    const [messages, setMessages] = useState([]);
    const [openDonateBox, setOpenDonateBox] = useState(true);
    const translate = useTranslate();
    const { isLoading, authenticated } = useAuthState();
    const theme = useTheme();
    const { data: upcomingEvents, isLoading: isLoadingEvents } = useGetList('events', { pagination: { page: 1, perPage: 8 }, sort: { field: 'start_at', order: 'ASC' }, filter: { start_date: NOW?.toLocaleDateString('fr-CA') } });
    const [locale] = useLocaleState();
    useAuthenticated();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getAccount();
                setAccount(response);

                const responseMessages = await fetchMessages();
                if (responseMessages.results) {
                    setMessages(responseMessages.results);
                }

                const responseStats = await fetchAdminDashboardStats();
                setStats(responseStats);
            } catch (error) {
                console.error(error);
            }
        };

        if (!isLoading && authenticated) {
            fetchData();
        }
    }, [isLoading, authenticated]);

    if (!authenticated) {
        return null;
    }

    const handleCloseDonateBox = () => {
        setOpenDonateBox(false);
    };

    return (
        <>
            {messages?.length > 0 && (
                <Box py={3}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={12} lg={8} item>
                            <Paper elevation={0} sx={{ padding: '15px', border: '1px solid #F1F5F6' }}>
                                {messages.map(message => {
                                    const content = (message.translations?.content && message.translations?.content[locale]) || message.content;
                                    return (
                                        <RichTextField record={{ content }} source="content" key={`message_${message.id}`} />
                                    );
                                })}
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            )}

            <Grid container spacing={2} sx={{ paddingY: '15px' }}>
                <Grid item lg={4} sm={6} xs={12}>
                    <Link to="/events">
                        <IconCard title={translate('Total')} value={stats?.events?.all} icon={<events.icon />} />
                    </Link>
                </Grid>
                <Grid item lg={4} sm={6} xs={12}>
                    <Link to="/events">
                        <IconCard title={translate('Upcoming')} value={stats?.events?.upcoming} icon={<events.icon />} />
                    </Link>
                </Grid>
                <Grid item lg={4} sm={6} xs={12}>
                    <Link to="/events">
                        <IconCard title={translate('Past')} value={stats?.events?.past} icon={<events.icon />} />
                    </Link>
                </Grid>
                {/*<Grid item lg={4} sm={6} xs={12}>
                    <Link to='/events?filter={"published":true}'>
                        <IconCard title={translate('Published')} value={stats?.events?.published} icon={<events.icon />} />
                    </Link>
                </Grid>
                <Grid item lg={4} sm={6} xs={12}>
                    <Link to='/events?filter={"status":"review"}'>
                        <IconCard title={translate('Waiting for validation')} value={stats?.events?.review} icon={<events.icon />} />
                    </Link>
                </Grid>
                <Grid item lg={4} sm={6} xs={12}>
                    <Link to='/events?filter={"status":"refused"}'>
                        <IconCard title={translate('Refused')} value={stats?.events?.refused} icon={<events.icon />} />
                    </Link>
                </Grid> */}
            </Grid>

            <Collapse in={openDonateBox}>
                <Box py={3}>
                    <Alert icon={false} onClose={handleCloseDonateBox} severity="success" sx={sxDonateBox} >
                        <Grid container spacing={3}>
                            <Grid xs={12} sm={12} lg={2} item>
                                <Typography variant="h5" sx={{ whiteSpace: 'pre-wrap' }}>{translate('Contribute to the project!', { _: 'Contribute \nto the project!'})}</Typography>
                            </Grid>
                            <Grid xs={12} sm={12} lg={8} item>
                                <Typography>{translate('Europe Remembers aims to give visibility to your events and is free to use ! However we need your contributions to help us maintain and keep this platform running.')}</Typography>
                            </Grid>
                            <Grid xs={12} sm={12} lg={2} item textAlign="right">
                                <DonateButton />
                            </Grid>
                        </Grid>
                    </Alert>
                </Box>
            </Collapse>

            {!isLoadingEvents && (
                <Box pb={3}>
                    <Typography variant="h5" color="primary" my={3}>{translate('My upcoming event(s)')}</Typography>
                    {upcomingEvents?.length > 0 ? (
                        <Grid container spacing={2}>
                            {upcomingEvents.map(event => (
                                <Grid xs={12} sm={6} lg={3} item key={`event_${event.id}`}>
                                    <EventCard record={event} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Grid container spacing={2} alignItems="stretch">
                            <Grid xs={12} sm={12} lg={5} item>
                                <Paper elevation={0} sx={{ padding: '30px 15px', backgroundColor: lighten(theme.palette.primary.main, 0.75), border: `1px solid ${theme.palette.primary.main}` }}>
                                    <Typography variant="h5" gutterBottom>{translate('Europe Remembers : How to create an event ?')}</Typography>
                                    <Typography mb={3}>{translate('Turn on subtitles (CC) to get instructions in your language.')}</Typography>
                                    <Box sx={sxVideoContainer}>
                                        <iframe
                                            title={translate('Europe Remembers : How to create an event ?')}
                                            src="https://www.youtube.com/embed/5elJ3wF2jbY"
                                            allowFullScreen
                                        />
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid xs={12} sm={12} lg={3} item display="flex">
                                <Paper
                                    elevation={0}
                                    sx={{
                                        padding: '30px 15px',
                                        backgroundColor: lighten(theme.palette.secondary.main, 0.75),
                                        border: `1px solid ${theme.palette.secondary.main}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between'
                                    }}
                                >
                                    <Box>
                                        <Typography variant="h5" gutterBottom>{translate('Join us and create an event')}</Typography>
                                        <Typography mb={3}>{translate('Once your event is created the Europe Members team will review your event. If everything is in order, you will be prompted by e-mail and you will be able to publish your event.')}</Typography>
                                    </Box>
                                    <IconButton icon={<AddIcon />} component={Link} to={`/events/create`} variant="contained" color="secondary">{translate('Create event')}</IconButton>
                                </Paper>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            )}
        </>
    );
};

export default Dashboard;
